import React, { createContext, useContext, useEffect, useState } from 'react';
import StorageService from '../services/storageService';

type ThemeContextType = {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'app_theme_preference';

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Initialize theme from storage or system preference
  useEffect(() => {
    const initializeTheme = async () => {
      try {
        // Try to get stored preference
        const storedTheme = await StorageService.getValue(THEME_STORAGE_KEY);

        if (storedTheme) {
          setIsDarkMode(storedTheme === 'dark');
        } else {
          // If no stored preference, use system preference
          const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          setIsDarkMode(systemPrefersDark);
          // Save the system preference
          await StorageService.storeValue(THEME_STORAGE_KEY, systemPrefersDark ? 'dark' : 'light');
        }
      } catch (error) {
        console.error('Failed to initialize theme:', error);
        // Fallback to light mode if there's an error
        setIsDarkMode(false);
      } finally {
        setIsInitialized(true);
      }
    };

    initializeTheme();
  }, []);

  // Apply dark mode class to html element
  useEffect(() => {
    if (!isInitialized) return;

    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Store the preference
    StorageService.storeValue(THEME_STORAGE_KEY, isDarkMode ? 'dark' : 'light').catch((error) =>
      console.error('Failed to store theme preference:', error),
    );
  }, [isDarkMode, isInitialized]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't set a preference
      StorageService.getValue(THEME_STORAGE_KEY).then((storedTheme) => {
        if (!storedTheme) {
          setIsDarkMode(e.matches);
        }
      });
    };

    // Add event listener (with compatibility check)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // For older browsers
      mediaQuery.addListener(handleChange);
    }

    return () => {
      // Clean up
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        // For older browsers
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  const toggleDarkMode = () => {
    setIsDarkMode((prev) => !prev);
  };

  return <ThemeContext.Provider value={{ isDarkMode, toggleDarkMode }}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
